<template>
  <div>
    <v-card block class="px-6 py-0 d-flex d-print-none flex-row justify-space-between" color="primary" dark
            :key="$_sideMenu" large sticky-offset="$_offset" sticky-z-index="99"
            style="border-top: 1px solid white !important" tile v-sticky>
      <div>
        <information-dialog-icon confirmClass="mr-3 mb-1 title" confirmColor="secondary darken-1"
                                 icon-color="white" title="Leads List" tclass="headline grey lighten-2 mb-8">
          <p>The Leads List shows firms with <strong>no professional-liability x-date</strong> (exdate_pl)
            for a producer to pursue.</p>
          <p><strong>Producer</strong> and <strong>State</strong> are required. Producer defaults to your
            username when it appears in the producer list. <strong>Firm Type</strong> is optional—leave it
            blank to include all firm types.</p>
          <p>After selections, press <strong>Enter</strong> or click LEADS<span class="hidden-sm-and-down"> LIST</span>.</p>
          <p v-if="$_sideMenu">For better print results, switch to top menu by clicking the lion in the upper-left corner.</p>
          <p>After the report appears below, click <v-icon>mdi-printer</v-icon> for <strong>print guidance</strong>, then print.</p>
        </information-dialog-icon>
        <button-tip btnclass="px-6" @click="getLeads()" :disabled="loading || !producer || !state"
                    hideSmDownAfter=" List" large text tile tip="Get leads data" txt="Leads"/>
      </div>
      <div>
        <button-tip btnclass="px-8" @click="printDiv()" :disabled="loading || !searchResultLoadedShow || !leadsData.length"
                    icolor="white" icon iname="mdi-printer" tip="Open print dialog"/>
      </div>
      <div>
        <button-tip btnclass="px-8" @click="clearSearch()" :disabled="loading" large text tile
                    tip="Reset Producer to default and clear State and Firm Type" txt="Reset"/>
      </div>
    </v-card>
    <v-card tile class="px-5 mx-0 pt-2 pb-1 mt-3 mb-2 d-print-none elevation-18">
      <v-row class="my-n1 mx-0 pl-0">
        <v-col class="d-flex justify-center" cols="12" md="4" xl="3">
          <v-row class="px-3" align="center" style="z-index: 89">
            <search-input ref="producerInput" @enter="getLeads()" :items="producerOptions"
                          :label="producerInputLabel" :inputProps="{ error: showProducerRequiredHint }"
                          :type="'VAutocomplete'" v-model="producer"/>
          </v-row>
        </v-col>
        <v-col class="d-flex justify-center" cols="12" md="4" xl="3">
          <v-row class="px-3" align="center" style="z-index: 89">
            <search-input ref="stateInput" @enter="getLeads()" :items="$_lists ? $_lists.states : []"
                          :label="stateInputLabel" :inputProps="{ error: showStateRequiredHint }"
                          :type="'VAutocomplete'" v-model="state"/>
          </v-row>
        </v-col>
        <v-col class="d-flex justify-center" cols="12" md="4" xl="3">
          <v-row class="px-3" align="center" style="z-index: 89">
            <search-input @enter="getLeads()" :items="$_lists ? $_lists.ftype : []"
                          label="Firm Type (optional)" :type="'VAutocomplete'" v-model="type"/>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
    <div class="px-4 pb-6" v-if="loading || searchResultLoadedShow">
      <div class="ma-6 leadsheader text-center" style="line-height: 1.2" v-if="leadsData.length">
        <h4 class="mb-0">Leads List</h4>
        <h5 class="mb-0">{{ reportFilterSummary }}</h5>
        <h5 class="mb-0">{{ leadsData.length }} {{ leadsData.length === 1 ? 'lead' : 'leads' }}</h5>
      </div>
      <v-data-table class="px-0 mx-0 mt-6 elevation-4 left med-table-rows prtleads striped-table leads-table-style"
                    :headers="headers"
                    hide-default-footer
                    :items="leadsData"
                    :items-per-page="-1"
                    :loading="loading"
                    loading-text="Loading... Please wait"
                    :mobile-breakpoint="0"
                    multi-sort
                    no-data-text="No unexdated leads found for that producer and state.">
        <template v-slot:item.fid="{ item }">
          <a v-if="item.fid && canFirmView" class="leads-fid-link" @click="$_viewFirmNoNotePopup(item.fid)">
            {{ item.fid }}
          </a>
          <span v-else>{{ item.fid }}</span>
        </template>
        <template v-slot:item.phone="{ item }">
          <span style="white-space: nowrap">{{ formatPhoneNumber(item.phone) }}</span>
        </template>
      </v-data-table>
    </div>

    <ErrorDialog ref="errorDialog"/>
    <action-dialog v-model="showPrintDialog" max-width="360" title="Before You Print" confirm-text="Continue to Print"
                   close-text="Cancel" @confirm="confirmPrint" @close="showPrintDialog = false">
      <p class="mb-2">You are printing all {{ leadsData.length }} lead(s) currently shown.</p>
      <p v-if="leadsData.length >= 500" class="mb-2 warning--text text--darken-2">
        Large list — print preview may take a while or slow the browser.
      </p>
      <p class="print-settings-alert mb-0"><strong>Use Landscape, Default margins, and Print backgrounds.</strong></p>
    </action-dialog>

  </div>
</template>
<script>
  import ActionDialog from "../ActionDialog";
  import ErrorDialog from "../ErrorDialog";
  import InformationDialogIcon from "../InformationDialogIcon";
  import { FirmsAPIService } from '@/servicehandlers/FirmsAPIService';
  import { ListsAPIService } from '@/servicehandlers/ListsAPIService';

  const firmsAPIService = new FirmsAPIService();
  const listsAPIService = new ListsAPIService();

  export default {
    name: 'Leads',
    components: { ActionDialog, ErrorDialog, InformationDialogIcon },

    data() {
      return {
        currentUsername: '',
        headers: [
          { value: 'fid', text: 'FID', width: '80px', align: 'end' },
          { value: 'sort', text: 'Sort Name', width: '340px' },
          { value: 'type', text: 'Firm Type', width: '180px' },
          { value: 'state', text: 'State', width: '90px' },
          { value: 'contact', text: 'Contact', width: '180px' },
          { value: 'phone', text: 'Phone', width: '130px' },
        ],
        leadsData: [],
        loading: false,
        producer: '',
        producerOptions: [],
        searchResultLoadedShow: false,
        showPrintDialog: false,
        showProducerRequiredHint: false,
        showStateRequiredHint: false,
        state: '',
        type: '',
      };
    },

    computed: {
      canFirmView() {
        return this.$_can('firm-view');
      },

      producerInputLabel() {
        if (this.showProducerRequiredHint) return 'Producer (required)';
        if (this.getDefaultProducer()) return 'Producer (required, defaults to you)';
        return 'Producer (required)';
      },

      stateInputLabel() {
        return this.showStateRequiredHint ? 'State (required)' : 'State';
      },

      reportFilterSummary() {
        const parts = [`Producer: ${this.producer}`, `State: ${this.state}`];
        if (this.type) parts.push(`Firm Type: ${this.type}`);
        return parts.join(' — ');
      },
    },

    watch: {
      producer() {// IF a filter changes THEN results no longer match selected search parameters, so don't show
        if (this.producer) this.showProducerRequiredHint = false;
        this.searchResultLoadedShow = false;
      },
      state() {
        if (this.state) this.showStateRequiredHint = false;
        this.searchResultLoadedShow = false;
      },
      type() {
        this.searchResultLoadedShow = false;
      },
    },

    created() {
      document.title = 'Leads List';
      this.currentUsername = this.$store.getters.username || '';
      this.loadProducers();
    },

    methods: {
      getLeads() {
        if (!this.producer) {
          this.showProducerRequiredHint = true;
          this.$nextTick(() => this.focusField('producerInput'));
          return;
        }
        if (!this.state) {
          this.showStateRequiredHint = true;
          this.$nextTick(() => this.focusField('stateInput'));
          return;
        }
        this.loading = true;
        const filters = { producer: this.producer, state: this.state, type: this.type || undefined };
        firmsAPIService.unexdatedLeads(filters, this.$router).then((leads) => {
          this.leadsData = Array.isArray(leads) ? leads : [];
          this.searchResultLoadedShow = true;
        }).catch((e) => {
          this.$_showErrorDialog({
            errorMessage: 'Failed to get Leads List data.',
            forDevelopers: e
          });
        }).finally(() => {
          this.loading = false;
        });
      },

      async loadProducers() {
        try {
          const rows = await listsAPIService.getActiveProducers(this.$router);
          const names = new Set();
          if (Array.isArray(rows)) {
            for (const row of rows) {
              const name = typeof row === 'string' ? row : (row && (row.short || row.value || row.text));
              if (name) names.add(`${name}`.trim());
            }
          }
          this.producerOptions = Array.from(names).filter(Boolean).sort((left, right) => left.localeCompare(right));
          if (!this.producer) this.producer = this.getDefaultProducer();
        } catch (e) {
          this.producerOptions = [];
          this.$_showErrorDialog({
            errorMessage: 'Failed to load producers.',
            forDevelopers: e
          });
        }
      },

      // 11 Jun 2026 JCH: case-insensitive match so default-to-me works regardless of stored casing (same as Renewal)
      getDefaultProducer() {
        if (!Array.isArray(this.producerOptions) || !this.currentUsername) return '';
        const normalized = `${this.currentUsername}`.trim().toLowerCase();
        return this.producerOptions.find((name) => `${name}`.trim().toLowerCase() === normalized) || '';
      },

      focusField(refName) {
        const input = this.$refs[refName];
        if (input && typeof input.focus === 'function') input.focus();
        else if (input && input.$el) {
          const innerInput = input.$el.querySelector('input');
          if (innerInput) innerInput.focus();
        }
      },

      formatPhoneNumber(value) {
        if (!value) return '';
        return String(value).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      },

      clearSearch() {
        this.loading = false;
        this.searchResultLoadedShow = false;
        this.showPrintDialog = false;
        this.leadsData = [];
        this.producer = this.getDefaultProducer();
        this.state = '';
        this.type = '';
        this.showProducerRequiredHint = false;
        this.showStateRequiredHint = false;
      },

      printDiv() {
        this.showPrintDialog = true;
      },

      confirmPrint() {
        this.showPrintDialog = false;
        window.print();
      },
    },
  }
</script>
<style>
  .print-settings-alert {
    color: #b71c1c;
    font-weight: 700;
  }
  /* page header style */
  .leadsheader {
    color: black;
    font-size: larger !important;
  }
  /* FID links to the firm popup */
  .leads-fid-link {
    text-decoration: underline;
  }
  /* table header style */
  .leads-table-style.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
    background-color: rgba(0, 0, 0, 0.12);/* grey header color helps hide strange vertical lines in print and pdf */
    border-bottom: 1px solid black;
    color: black;
  }
  /* add shading to alternate table rows */
  .striped-table tbody tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, .06);
  }
  /* position and style table for printing */
  @media print
  {
    body * { visibility: hidden }
    html, body { height: 100% }
    .leadsheader * { visibility: visible }
    .leadsheader { position: absolute !important; top: -60px !important; left: -40px !important; width: 98% !important }
    .prtleads * { visibility: visible }
    .prtleads { position: absolute !important; top: 25px !important; width: 98% !important; height: auto !important }
  }
  @page { margin: .5in .4in .5in .3in !important }
</style>
